import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '@/services/auth.service';
// No type imports needed since we're using a literal object
import './RegistrationPage.scss';
import languageIcon from '../../assets/icons/language-icon-gray.svg';
import dangerIcon from '../../assets/icons/danger-icon.svg';
import { AccountStep, AccountStepRef } from '@/components/RegistrationForm/steps/AccountStep';
import { CompanyStep, CompanyStepRef } from '@/components/RegistrationForm/steps/CompanyStep';
import { DealerStep, DealerStepRef } from '@/components/RegistrationForm/steps/DealerStep';
import { SecurityStep, SecurityStepRef } from '@/components/RegistrationForm/steps/SecurityStep';
import RegistrationCompleted from '@/components/RegistrationForm/RegistrationCompleted';
import { Loader } from '@/components/Loader/Loader';

// Import step icons
import step1Icon from '../../assets/icons/registration-form-step-icons/step-1-incomplete.svg';
import step2Icon from '../../assets/icons/registration-form-step-icons/step-2-incomplete.svg';
import step3Icon from '../../assets/icons/registration-form-step-icons/step-3-incomplete.svg';
import step4Icon from '../../assets/icons/registration-form-step-icons/step-4-incomplete.svg';
import completedStepIcon from '../../assets/icons/registration-form-step-icons/completed-step.svg';

type Step = 'account' | 'company' | 'dealer' | 'security';

interface StepConfig {
  id: Step;
  title: string;
  icon: string;
}

export const RegistrationPage: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  // No longer need navigate since we're not redirecting automatically
  const [currentStep, setCurrentStep] = useState<Step>('account');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const steps: StepConfig[] = [
    { id: 'account', title: t('registration.steps.account'), icon: step1Icon },
    { id: 'company', title: t('registration.steps.company'), icon: step2Icon },
    { id: 'dealer', title: t('registration.steps.dealer'), icon: step3Icon },
    { id: 'security', title: t('registration.steps.security'), icon: step4Icon }
  ];

  const accountStepRef = useRef<AccountStepRef>(null);
  const companyStepRef = useRef<CompanyStepRef>(null);
  const dealerStepRef = useRef<DealerStepRef>(null);
  const securityStepRef = useRef<SecurityStepRef>(null);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const handleBack = () => {
    const currentIndex = steps.findIndex(step => step.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].id);
      // Clear any errors when navigating back
      setError(null);
    }
  };

  // Function to ensure all refs are initialized
  const ensureRefsInitialized = () => {
    return new Promise<void>((resolve, reject) => {
      // Check if refs are initialized
      if (accountStepRef.current && companyStepRef.current && 
          dealerStepRef.current && securityStepRef.current) {
        resolve();
        return;
      }
      
      // If not initialized, reject with error
      reject(new Error('Form not fully loaded. Please try again.'));
    });
  };

  const handleNext = async () => {
    const currentIndex = steps.findIndex(step => step.id === currentStep);

    if (currentIndex === steps.length - 1) {
      try {
        setIsLoading(true);
        setError(null);
        
        // Ensure all refs are initialized
        await ensureRefsInitialized();
        
        // At this point, we've verified all refs are initialized
        // TypeScript still warns about null, so we need to use non-null assertion
        // Get data from each step and validate it (even if we don't use all fields now)
        // This ensures all steps have valid data before proceeding
        accountStepRef.current!.getFormData(); // Validate account data
        // Submit each step's form
        await accountStepRef.current!.submitForm();
        await companyStepRef.current!.submitForm();
        await dealerStepRef.current!.submitForm();
        await securityStepRef.current!.submitForm();

        // Get form data from each step
        const accountData = accountStepRef.current!.getFormData();
        const companyData = companyStepRef.current!.getFormData();
        // Get dealer data with remoteObjectIds
        const dealerData = dealerStepRef.current!.getFormData();

        // Format URLs properly
        const formatUrl = (url: string | undefined) => {
          if (!url) return undefined;
          if (url.trim() === '') return undefined;
          return url.match(/^https?:\/\//i) ? url : `https://${url.replace(/^www\./, '')}`;
        };
        
        // Prepare base account information
        const accountInfo: any = {
          "firstname": accountData.firstName,
          "lastname": accountData.lastName,
          "professionalEmail": accountData.professionalEmail,
          "profession": accountData.profession,
          "language": i18n.language === 'fr' ? 'French' : 'English'
        };

        // Only add optional fields if they have values
        if (accountData.personalEmail?.trim()) {
          accountInfo.personalEmail = accountData.personalEmail.trim();
        }

        const websiteUrl = formatUrl(accountData.website);
        if (websiteUrl) {
          accountInfo.websiteOrPortfolio = websiteUrl;
        }

        const linkedinUrl = formatUrl(accountData.linkedin);
        if (linkedinUrl) {
          accountInfo.linkedinProfile = linkedinUrl;
        }

        const instagramUrl = formatUrl(accountData.instagram);
        if (instagramUrl) {
          accountInfo.instagramProfile = instagramUrl;
        }
        
        // Prepare registration data
        const registrationData: any = {
          "accountInformation": accountInfo,
          "companyInformation": {
            "accountId": companyData.remoteObjectId,
            "companyName": companyData.companyName,
            "professionalPhoneNumber": companyData.companyPhone,
            "companyStreet": companyData.companyStreet,
            "companyUnit": companyData.unit || '',
            "companyPostalCode": companyData.postalCode || '',
            "companyCity": companyData.city || '',
            "companyProvince": companyData.province || '',
            "companyCountry": companyData.country || 'Canada'
          },
          "securityInformation": {
            "username": securityStepRef.current!.getFormData().username,
            "password": securityStepRef.current!.getFormData().password
          }
        };

        // Only include dealerInformation if at least one field has a value
        // Temporary workaround until backend validation is fixed
        if (dealerData.dealerCompanyRemoteId || dealerData.dealerRepresentativeRemoteId) {
          registrationData.dealerInformation = {};
          if (dealerData.dealerCompanyRemoteId) {
            registrationData.dealerInformation.primaryDealer = dealerData.dealerCompanyRemoteId;
          }
          if (dealerData.dealerRepresentativeRemoteId) {
            registrationData.dealerInformation.salesRepresentative = dealerData.dealerRepresentativeRemoteId;
          }
        }

        // Submit registration
        await authService.register(registrationData);
        
        // Set registration as completed and show the completion page
        // No automatic redirection - let the user click the button in RegistrationCompleted
        setRegistrationCompleted(true);

      } catch (error) {
        console.error('Registration failed:', error);
        
        // Type guard for the error structure
        interface ApiError {
          message: string | string[];
          error?: {
            data?: {
              message?: string;
            };
          };
        }
        
        const errorData = error as ApiError;
        
        // Function to translate backend validation errors to user-friendly messages
        const translateBackendError = (errorMsg: string): string => {
          // Dealer information errors
          if (errorMsg.includes('dealerInformation.primaryDealer')) {
            return t('registration.error.dealerRequired');
          }
          if (errorMsg.includes('dealerInformation.salesRepresentative')) {
            return t('registration.error.salesRepRequired');
          }
          
          // Security/username errors
          if (errorMsg.includes('securityInformation.username') && errorMsg.includes('longer than or equal to 4')) {
            return t('registration.error.usernameMinLength');
          }
          if (errorMsg.includes('securityInformation.username') && errorMsg.includes('should not be empty')) {
            return t('registration.error.usernameRequired');
          }
          
          // Password errors
          if (errorMsg.includes('securityInformation.password') && errorMsg.includes('longer than or equal to 8')) {
            return t('registration.error.passwordMinLength');
          }
          if (errorMsg.includes('securityInformation.password') && errorMsg.includes('should not be empty')) {
            return t('registration.error.passwordRequired');
          }
          
          // Email errors
          if (errorMsg.includes('email') && errorMsg.includes('must be an email')) {
            return t('registration.error.email');
          }
          
          // Return generic error if no match
          return t('registration.error.registrationFailed');
        };
        
        // Check for duplicate email error
        // Backend returns "duplicates value" (not "duplicate value found")
        if (
          errorData.error?.data?.message?.includes('Personal_Email__c duplicates value') ||
          errorData.error?.data?.message?.includes('Professional_Email__c duplicates value')
        ) {
          setError(t('registration.error.emailAlreadyExists'));
        } 
        // Check for duplicate username error
        else if (errorData.error?.data?.message?.includes('Username__c duplicates value')) {
          setError(t('registration.error.usernameAlreadyExists'));
        } 
        else if (errorData.message) {
          // Handle array of error messages from backend validation
          if (Array.isArray(errorData.message)) {
            const translatedErrors = errorData.message.map(translateBackendError);
            // Show only the first error to keep it simple
            setError(translatedErrors[0]);
          } else {
            // Single error message
            setError(translateBackendError(errorData.message));
          }
        } else {
          setError(t('registration.error.registrationFailed'));
        }
        
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Clear any previous errors
    setError(null);
    
    // Check if current step is valid before proceeding
    if (currentStep === 'account' && accountStepRef.current) {
      const isValid = await accountStepRef.current.submitForm();
      if (!isValid) return;
    } else if (currentStep === 'company' && companyStepRef.current) {
      const isValid = companyStepRef.current.submitForm();
      if (!isValid) return;
    } else if (currentStep === 'dealer' && dealerStepRef.current) {
      const isValid = dealerStepRef.current.submitForm();
      if (!isValid) return;
    } else if (currentStep === 'security' && securityStepRef.current) {
      const isValid = await securityStepRef.current.submitForm();
      if (!isValid) return;
    }

    setCurrentStep(steps[currentIndex + 1].id);
  };

  // We'll keep the registration page layout even when completed

  return (
    <div className="registration-page">
      {isLoading && (
        <Loader variant="overlay" />
      )}
      <div className="registration-page__content">
        <header className="registration-page__header">
          <Link to="/auth" className="registration-page__back-button">
            ←
          </Link>
          <div className="registration-page__steps">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div 
                  className={`registration-page__step ${currentStep === step.id ? 'active' : ''} ${
                    steps.findIndex(s => s.id === currentStep) > index ? 'completed' : ''
                  } ${registrationCompleted && step.id === 'security' ? 'completed' : ''}`}
                >
                  <div className="registration-page__step-icon">
                    <img 
                      src={(steps.findIndex(s => s.id === currentStep) > index || (registrationCompleted && step.id === 'security')) ? completedStepIcon : step.icon} 
                      alt={step.title} 
                    />
                  </div>
                  <span className="registration-page__step-title">{step.title}</span>
                </div>
                {index < steps.length - 1 && <div className="registration-page__step-connector" />}
              </React.Fragment>
            ))}
          </div>
          <button
            className="language-toggle"
            onClick={toggleLanguage}
            aria-label={t('nav.changeLanguage')}
          >
            <img src={languageIcon} alt="" />
            <span>{i18n.language.toUpperCase()}</span>
          </button>
        </header>

        <main className="registration-page__main">
          <div className="registration-page__form">
            {registrationCompleted ? (
              // Show the registration completed component when registration is completed
              <RegistrationCompleted />
            ) : (
              // Otherwise show the current step
              <>
                {/* Always render all steps but only show the current one */}
                <div style={{ display: currentStep === 'account' ? 'block' : 'none' }}>
                  <AccountStep
                    ref={accountStepRef}
                    initialData={{}}
                    onNext={() => {}}
                  />
                </div>
                <div style={{ display: currentStep === 'company' ? 'block' : 'none' }}>
                  <CompanyStep
                    ref={companyStepRef}
                    initialData={{}}
                    onNext={() => {}}
                  />
                </div>
                <div style={{ display: currentStep === 'dealer' ? 'block' : 'none' }}>
                  <DealerStep
                    ref={dealerStepRef}
                    initialData={{}}
                  />
                </div>
                <div style={{ display: currentStep === 'security' ? 'block' : 'none' }}>
                  <SecurityStep
                    ref={securityStepRef}
                    initialData={{}}
                    onTermsChange={setTermsAccepted}
                  />
                </div>
              </>
            )}
          </div>

          {error && (
            <div className="registration-page__error">
              <div className="registration-page__error-content">
                <img src={dangerIcon} alt="Error" className="error-icon" />
                <span>{error}</span>
              </div>
            </div>
          )}
          
          {!registrationCompleted && (
            <div className="registration-page__form-actions">
              <div className="form-actions-buttons">
                <button
                  type="button"
                  onClick={handleBack}
                  className="form-button form-button--secondary"
                  disabled={currentStep === 'account'}
                >
                  {t('registration.previous')}
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="form-button form-button--primary"
                  disabled={currentStep === 'security' && !termsAccepted}
                >
                  {currentStep === 'security' ? t('registration.completeRegistration') : t('registration.next')}
                </button>
              </div>
            </div>
          )}
        </main>
        <footer className="registration-page__footer">
          <p className="registration-page__disclaimer">
            {t('footer.disclaimer')}
          </p>
          <Link to="/guest-contact" className="contact-link">
            {t('authPage.needHelp')} <span className="underline-hover">{t('authPage.contactUs')}</span>
          </Link>
        </footer>
      </div>
    </div>
  );
};
